import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { Button, ConfirmDialog, Labeled, Modal, TextInput, Toggle } from '@tes/ui';
import { api, ApiError } from '../../lib/api';
import { runSequential, type BulkOutcome } from '../../lib/bulk';
import { messageOf } from '../../lib/errors';
import type { TemplateSummary } from '../../lib/types';

const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

/**
 * Completed envelopes are evidence: their signers, values and audit chain go
 * with the documents. The server refuses unless `force=true`, so the dialog
 * makes that an explicit, separately ticked decision.
 */
function CompletedSigningWarning({ count }: { count: number }) {
  if (!count) return null;
  return (
    <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      <ShieldAlert size={16} className="mt-0.5 shrink-0" />
      <span>
        <span className="font-medium">
          {count} completed {plural(count, 'envelope')} {plural(count, 'was', 'were')} signed
        </span>{' '}
        from {count === 1 ? 'this template' : 'these templates'}. Deleting destroys{' '}
        {count === 1 ? 'its' : 'their'} signed documents and audit trail. Archiving keeps them.
      </span>
    </div>
  );
}

function PendingSigningWarning({ count }: { count: number }) {
  if (!count) return null;
  return (
    <div className="flex gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <span>
        {count} {plural(count, 'envelope is', 'envelopes are')} still awaiting signatures. Their
        signing links will stop working.
      </span>
    </div>
  );
}

const forceLabel = (count: number) =>
  `Also destroy the signing history of ${count} completed ${plural(count, 'envelope')}`;

/**
 * Typed confirmation for an irreversible delete. The preview is fetched fresh so
 * the counts shown are the ones the server is about to destroy, and the name must
 * be retyped before the button unlocks.
 */
export function DeleteTemplateDialog({
  template,
  onClose,
  onDeleted,
}: {
  template: TemplateSummary;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [force, setForce] = useState(false);
  const { data: preview, isLoading, error: previewError } = useQuery({
    queryKey: ['template-deletion-preview', template.id],
    queryFn: () => api.templateDeletionPreview(template.id),
    staleTime: 0,
  });

  const mutation = useMutation({
    mutationFn: () => api.deleteTemplate(template.id, { force }),
    onSuccess: onDeleted,
  });

  const completed = preview?.completedSigning ?? 0;

  return (
    <ConfirmDialog
      title="Delete template"
      onClose={onClose}
      onConfirm={() => mutation.mutate()}
      confirmWord={template.name}
      busy={mutation.isPending}
      // No preview = no idea what this destroys; don't offer the button.
      disabled={!preview}
      acknowledge={
        completed > 0
          ? { label: forceLabel(completed), checked: force, onChange: setForce }
          : undefined
      }
      error={mutation.error ? messageOf(mutation.error, 'Delete failed') : null}
    >
      <p className="text-sm text-slate-600">
        Permanently delete <span className="font-medium text-slate-900">{template.name}</span>?
      </p>

      {isLoading ? (
        <p className="text-sm text-slate-400">Checking what this would remove…</p>
      ) : previewError ? (
        <p className="text-sm text-red-600">{messageOf(previewError, 'Could not load preview')}</p>
      ) : preview ? (
        <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
          <p className="mb-1.5 font-medium text-slate-700">This also deletes:</p>
          <ul className="list-inside list-disc space-y-0.5">
            <li>
              <span className="font-medium text-slate-900">{preview.documents}</span> generated{' '}
              {plural(preview.documents, 'document')}
            </li>
            <li>
              <span className="font-medium text-slate-900">{preview.envelopes}</span>{' '}
              {plural(preview.envelopes, 'envelope')} left with no documents
            </li>
            <li>
              <span className="font-medium text-slate-900">{preview.versions}</span> published{' '}
              {plural(preview.versions, 'version')}
            </li>
          </ul>
        </div>
      ) : null}

      <PendingSigningWarning count={preview?.pendingSigning ?? 0} />
      <CompletedSigningWarning count={completed} />

      <p className="text-sm text-slate-500">This cannot be undone.</p>
    </ConfirmDialog>
  );
}

/**
 * Bulk delete. Retyping every name would be absurd, so the gate is the word
 * "delete" — the friction that matters here is the itemised preview above it.
 *
 * That preview comes from one server call over the whole set rather than N
 * single-template previews summed: an envelope holding documents from two of
 * the doomed templates is orphaned by the pair, and each individual preview
 * would report it as surviving.
 */
export function BulkDeleteTemplatesDialog({
  templates,
  onClose,
  onDeleted,
}: {
  templates: TemplateSummary[];
  onClose: () => void;
  onDeleted: (results: BulkOutcome[]) => void;
}) {
  const [force, setForce] = useState(false);
  const [done, setDone] = useState(0);
  const ids = useMemo(() => templates.map((t) => t.id), [templates]);

  const { data: preview, isLoading, error: previewError } = useQuery({
    queryKey: ['templates-deletion-preview', ids],
    queryFn: () => api.templatesDeletionPreview(ids),
    staleTime: 0,
  });

  const mutation = useMutation({
    mutationFn: () =>
      runSequential(
        templates,
        (t) => ({ id: t.id, name: t.name }),
        (t) => api.deleteTemplate(t.id, { force }),
        (n) => setDone(n),
      ),
    onSuccess: onDeleted,
  });

  const busy = mutation.isPending;
  const totals = preview?.totals;
  const completed = totals?.completedSigning ?? 0;

  return (
    <ConfirmDialog
      title={`Delete ${templates.length} ${plural(templates.length, 'template')}`}
      onClose={onClose}
      onConfirm={() => mutation.mutate()}
      confirmWord="delete"
      caseSensitive={false}
      busy={busy}
      disabled={!preview}
      size="lg"
      acknowledge={
        completed > 0
          ? { label: forceLabel(completed), checked: force, onChange: setForce }
          : undefined
      }
      error={mutation.error ? messageOf(mutation.error, 'Delete failed') : null}
      footerStart={busy ? `Deleting ${done} of ${templates.length}…` : undefined}
    >
      {isLoading ? (
        <p className="text-sm text-slate-400">Checking what this would remove…</p>
      ) : previewError ? (
        <p className="text-sm text-red-600">{messageOf(previewError, 'Could not load preview')}</p>
      ) : totals ? (
        <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
          <p className="mb-1.5 font-medium text-slate-700">This permanently deletes:</p>
          <ul className="list-inside list-disc space-y-0.5">
            <li>
              <span className="font-medium text-slate-900">{totals.templates}</span>{' '}
              {plural(totals.templates, 'template')} and{' '}
              <span className="font-medium text-slate-900">{totals.versions}</span> published{' '}
              {plural(totals.versions, 'version')}
            </li>
            <li>
              <span className="font-medium text-slate-900">{totals.documents}</span> generated{' '}
              {plural(totals.documents, 'document')}
            </li>
            <li>
              <span className="font-medium text-slate-900">{totals.envelopes}</span>{' '}
              {plural(totals.envelopes, 'envelope')} left with no documents
            </li>
          </ul>
        </div>
      ) : null}

      <PendingSigningWarning count={totals?.pendingSigning ?? 0} />
      <CompletedSigningWarning count={completed} />

      {!!preview?.missing.length && (
        <p className="text-sm text-amber-700">
          {preview.missing.length} of these no longer exist and will be skipped.
        </p>
      )}

      {preview && (
        <div className="max-h-48 overflow-auto rounded-lg border border-slate-200">
          <table className="w-full text-[13px]">
            <tbody className="divide-y divide-slate-100">
              {preview.templates.map((t) => (
                <tr key={t.templateId}>
                  <td className="px-3 py-1.5">{t.name}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right text-slate-500">
                    {t.documents} {plural(t.documents, 'doc')} · {t.versions}{' '}
                    {plural(t.versions, 'version')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-sm text-slate-500">This cannot be undone.</p>
    </ConfirmDialog>
  );
}

export function NewTemplateDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [requiresSignature, setRequiresSignature] = useState(true);
  const [file, setFile] = useState<File | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new ApiError(400, { message: 'Choose a PDF or image file' });
      const form = new FormData();
      form.append('file', file);
      form.append('name', name);
      form.append('requiresSignature', String(requiresSignature));
      return api.createTemplate(form);
    },
    onSuccess: (t) => onCreated(t.id),
  });

  return (
    <Modal title="New template" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <Labeled label="Name">
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-autofocus
          />
        </Labeled>
        <Labeled label="Base file (PDF, PNG, or JPEG)">
          <input
            type="file"
            accept="application/pdf,image/png,image/jpeg"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="w-full text-sm"
            required
          />
        </Labeled>
        <Toggle label="Requires signature" checked={requiresSignature} onChange={setRequiresSignature} />
        {mutation.error && (
          <p className="text-sm text-red-600">{messageOf(mutation.error, 'Failed to create')}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Creating…' : 'Create & edit'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
